import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Plus, Video, AlertCircle, RefreshCcw, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const examTypes = [
  { value: 'all', label: 'All Examinations' },
  { value: 'CBEAM', label: 'CBEAM - Aesthetic Medicine' },
  { value: 'CBELM', label: 'CBELM - Longevity Medicine' },
  { value: 'CBERM', label: 'CBERM - Regenerative Medicine' },
  { value: 'CBEAD', label: 'CBEAD - Aesthetic Dentistry' },
  { value: 'CBENA', label: 'CBENA - Aesthetic Nursing' },
  { value: 'CBEAA', label: 'CBEAA - Clinical Aesthetic Assistance' },
];

export default function VivaTab({ 
  token, 
  allocatedCandidates, 
  vivaSchedules, 
  fetchVivaSchedules 
}) {
  const [examTypeFilter, setExamTypeFilter] = useState('all');
  const [showVivaScheduleForm, setShowVivaScheduleForm] = useState(false);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [selectedVivaForReschedule, setSelectedVivaForReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ new_date: '', new_time: '', reason: '' });
  
  const [vivaScheduleForm, setVivaScheduleForm] = useState({
    candidate_id: '',
    candidate_name: '',
    candidate_email: '',
    exam_date: '',
    exam_time: '',
    location: '',
    is_online: false,
    notes: ''
  });

  const handleScheduleViva = async () => {
    if (!vivaScheduleForm.candidate_id || !vivaScheduleForm.exam_date || !vivaScheduleForm.exam_time) {
      toast.error('Please select candidate, date and time');
      return;
    }
    try {
      // If online viva, create Google Meet link first
      let meetingLink = null;
      if (vivaScheduleForm.is_online) {
        try {
          const meetRes = await axios.post(`${API}/examiner/google-meet/create`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          meetingLink = meetRes.data.meeting_uri;
          toast.info('Google Meet link created');
        } catch (meetErr) {
          console.error('Failed to create meeting link:', meetErr);
        }
      }
      
      await axios.post(`${API}/examiner/schedule-viva`, {
        ...vivaScheduleForm,
        meeting_link: meetingLink
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Viva examination scheduled successfully' + (meetingLink ? ' with Google Meet link' : ''));
      setShowVivaScheduleForm(false);
      setVivaScheduleForm({ candidate_id: '', candidate_name: '', candidate_email: '', exam_date: '', exam_time: '', location: '', is_online: false, notes: '' });
      fetchVivaSchedules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule viva'); }
  };

  const handleVivaReschedule = async () => {
    if (!selectedVivaForReschedule || !rescheduleData.new_date || !rescheduleData.new_time) {
      toast.error('Please fill in new date and time');
      return;
    }
    try {
      await axios.post(`${API}/examiner/vivas/${selectedVivaForReschedule.id}/reschedule`, rescheduleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Viva rescheduled successfully');
      setShowRescheduleForm(false);
      setSelectedVivaForReschedule(null);
      setRescheduleData({ new_date: '', new_time: '', reason: '' });
      fetchVivaSchedules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule viva'); }
  };

  // Get pending candidates (not yet scheduled)
  const scheduledCandidateIds = vivaSchedules.map(v => v.candidate_id);
  let pendingCandidates = allocatedCandidates.filter(c => {
    const candidateId = c.candidate?.id || c.user_id;
    return !scheduledCandidateIds.includes(candidateId);
  });

  // Apply examination type filter
  if (examTypeFilter !== 'all') {
    pendingCandidates = pendingCandidates.filter(c => {
      const examValue = typeof c.examination === 'object' ? c.examination?.type || c.examination?.name : c.examination;
      return c.examination_type === examTypeFilter || 
        c.exam_type === examTypeFilter ||
        examValue === examTypeFilter;
    });
  }

  return (
    <div className="p-4" data-testid="viva-tab-content">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">My Viva Examinations</h3>
          <p className="text-sm text-slate-500">Schedule and manage viva sessions for your allocated candidates</p>
        </div>
        <div className="flex gap-2">
          <Select value={examTypeFilter} onValueChange={setExamTypeFilter}>
            <SelectTrigger className="w-[200px] rounded-sm text-sm h-9">
              <SelectValue placeholder="Filter by exam type" />
            </SelectTrigger>
            <SelectContent>
              {examTypes.map(et => (
                <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            size="sm" 
            onClick={() => setShowVivaScheduleForm(!showVivaScheduleForm)} 
            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Schedule Viva
          </Button>
        </div>
      </div>

      {/* Pending Candidates Section */}
      {pendingCandidates.length > 0 && (
        <Card className="imcac-card mb-4 border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <h4 className="font-semibold text-[#1B2A4A]">Candidates Pending Viva Scheduling ({pendingCandidates.length})</h4>
            </div>
            <div className="space-y-2">
              {pendingCandidates.map(c => {
                const candidateId = c.candidate?.id || c.user_id;
                const candidateName = c.candidate?.name || c.full_name || 'Unknown';
                const candidateEmail = c.candidate?.email || c.email || '';
                const examType = c.examination_type || c.exam_type || 
                  (typeof c.examination === 'object' ? c.examination?.type || c.examination?.name : c.examination) || '';
                return (
                  <div key={candidateId} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-sm font-bold">
                        {candidateName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#1B2A4A]">{candidateName}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-500">{candidateEmail}</p>
                          {examType && <Badge variant="outline" className="text-xs h-5">{examType}</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                      onClick={() => {
                        setVivaScheduleForm({
                          ...vivaScheduleForm,
                          candidate_id: candidateId,
                          candidate_name: candidateName,
                          candidate_email: candidateEmail
                        });
                        setShowVivaScheduleForm(true);
                      }}
                      data-testid={`schedule-viva-${candidateId}`}
                    >
                      <Calendar className="h-4 w-4 mr-1" /> Schedule Now
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Form */}
      {showVivaScheduleForm && (
        <Card className="imcac-card mb-4">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">Schedule Viva Examination</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Candidate</Label>
                <Select
                  value={vivaScheduleForm.candidate_id}
                  onValueChange={v => {
                    const candidate = allocatedCandidates.find(c => c.candidate?.id === v || c.user_id === v);
                    setVivaScheduleForm({
                      ...vivaScheduleForm,
                      candidate_id: v,
                      candidate_name: candidate?.candidate?.name || candidate?.full_name || '',
                      candidate_email: candidate?.candidate?.email || candidate?.email || ''
                    });
                  }}
                >
                  <SelectTrigger className="rounded-sm mt-1 text-sm"><SelectValue placeholder="Select candidate" /></SelectTrigger>
                  <SelectContent>
                    {allocatedCandidates.map(c => (
                      <SelectItem key={c.candidate?.id || c.user_id} value={c.candidate?.id || c.user_id}>
                        {c.candidate?.name || c.full_name} ({c.candidate?.email || c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Candidate Email</Label>
                <Input
                  value={vivaScheduleForm.candidate_email}
                  onChange={e => setVivaScheduleForm({...vivaScheduleForm, candidate_email: e.target.value})}
                  placeholder="candidate@email.com"
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Exam Date</Label>
                <Input
                  type="date"
                  value={vivaScheduleForm.exam_date}
                  onChange={e => setVivaScheduleForm({...vivaScheduleForm, exam_date: e.target.value})}
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Exam Time</Label>
                <Input
                  type="time"
                  value={vivaScheduleForm.exam_time}
                  onChange={e => setVivaScheduleForm({...vivaScheduleForm, exam_time: e.target.value})}
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Location</Label>
                <Input
                  value={vivaScheduleForm.location}
                  onChange={e => setVivaScheduleForm({...vivaScheduleForm, location: e.target.value})}
                  placeholder="IMCAC Examination Center / Online"
                  className="rounded-sm mt-1 text-sm"
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox
                  id="is_online"
                  checked={vivaScheduleForm.is_online}
                  onCheckedChange={checked => setVivaScheduleForm({...vivaScheduleForm, is_online: checked})}
                />
                <Label htmlFor="is_online" className="text-sm">Online Viva (Google Meet link will be created)</Label>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes (Optional)</Label>
                <Textarea
                  value={vivaScheduleForm.notes}
                  onChange={e => setVivaScheduleForm({...vivaScheduleForm, notes: e.target.value})}
                  placeholder="Any special instructions..."
                  className="rounded-sm mt-1 text-sm"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleScheduleViva} className="bg-[#1B2A4A] text-white rounded-sm">
                <Calendar className="h-4 w-4 mr-1" /> Schedule Viva
              </Button>
              <Button variant="outline" onClick={() => setShowVivaScheduleForm(false)} className="rounded-sm">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Vivas List */}
      {vivaSchedules.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Video className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No viva examinations scheduled yet.</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-xs font-bold">Candidate</TableHead>
                <TableHead className="text-xs font-bold">Date & Time</TableHead>
                <TableHead className="text-xs font-bold">Location</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vivaSchedules.map((viva) => (
                <TableRow key={viva.id}>
                  <TableCell>
                    <p className="font-medium text-[#1B2A4A]">{viva.candidate_name}</p>
                    <p className="text-xs text-slate-500">{viva.candidate_email}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{viva.exam_date ? new Date(viva.exam_date).toLocaleDateString() : 'TBD'}</p>
                    <p className="text-xs text-slate-500">{viva.exam_time || 'Time TBD'}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{viva.location || 'TBD'}</p>
                    {viva.meeting_link && (
                      <a href={viva.meeting_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" /> Google Meet
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${
                      viva.status === 'completed' ? 'bg-green-100 text-green-700' :
                      viva.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {viva.status || 'scheduled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm"
                        onClick={() => {
                          setSelectedVivaForReschedule(viva);
                          setRescheduleData({ new_date: '', new_time: '', reason: '' });
                          setShowRescheduleForm(true);
                        }}
                      >
                        <RefreshCcw className="h-3 w-3 mr-1" /> Reschedule
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleForm && selectedVivaForReschedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRescheduleForm(false)}>
          <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-lg">Reschedule Viva</CardTitle>
              <CardDescription>Rescheduling for {selectedVivaForReschedule.candidate_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">New Date</Label>
                  <Input
                    type="date"
                    value={rescheduleData.new_date}
                    onChange={e => setRescheduleData({...rescheduleData, new_date: e.target.value})}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">New Time</Label>
                  <Input
                    type="time"
                    value={rescheduleData.new_time}
                    onChange={e => setRescheduleData({...rescheduleData, new_time: e.target.value})}
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason for Reschedule</Label>
                <Textarea
                  value={rescheduleData.reason}
                  onChange={e => setRescheduleData({...rescheduleData, reason: e.target.value})}
                  placeholder="Brief reason for rescheduling..."
                  className="rounded-sm mt-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={handleVivaReschedule}
                  className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                >
                  <RefreshCcw className="h-4 w-4 mr-1" /> Confirm Reschedule
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowRescheduleForm(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
